import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { createMiniGroup, deleteMiniGroup, updateMiniGroup } from '@/store/chat/actions';

import { selectChat } from '@/store/chat/selectors';

import { Button, ColorPicker, Form, Input, Typography } from 'antd';

import ChatUser from '../../Messages/components/ChatUser';

import PropTypes from 'prop-types';

import styles from '../ChatsList.module.scss';

const MAX_LENGTH_NAME = 255;

const NewMiniGroup = ({ onCancel, group, onSubmit }) => {
  const [selectedUsers, setSelectedUser] = useState([]);
  const [form] = Form.useForm();

  const groupName = Form.useWatch('groupName', form);
  const groupColor = Form.useWatch('groupColor', form);
  const dispatch = useDispatch();

  const { chatUsers, selectedUser } = useSelector(selectChat);
  const { t } = useTranslation('chat');

  const groupUser = chatUsers?.filter(user => user?.groups?.groupId === group?.groupId).map(user => user.id);

  useEffect(() => {
    if (group) {
      form.setFieldValue('groupName', group.groupName);
      form.setFieldValue('groupColor', group.groupColor);
      setSelectedUser(groupUser);
    }
  }, [group, form, chatUsers]);

  const isButtonDisabled =
    !groupName ||
    !groupColor ||
    selectedUsers.length === 0 ||
    !groupName.trim().length ||
    groupName.length > MAX_LENGTH_NAME ||
    (groupName === group?.groupName &&
      groupColor === group?.groupColor &&
      groupUser.toString() === selectedUsers.toString());

  const onSelectUser = userId => {
    setSelectedUser(prevArr => {
      if (prevArr.includes(userId)) {
        return prevArr.filter(n => n !== userId);
      } else {
        return [...prevArr, userId];
      }
    });
  };

  const onFinish = values => {
    const dto = {
      groupName: values.groupName,
      groupColor: typeof values.groupColor === 'string' ? values.groupColor : values.groupColor.toHexString(),
      userIds: selectedUsers,
    };

    if (group) {
      dispatch(updateMiniGroup(dto, selectedUser.chatId, group?.groupId, onSubmit));
    } else {
      dispatch(createMiniGroup(dto, selectedUser.chatId, onSubmit));
    }
  };

  const onDelete = () => {
    dispatch(deleteMiniGroup(selectedUser.chatId, group.groupId, onSubmit));
  };

  const filteredChatUsers = chatUsers.filter(
    user => user?.id !== selectedUser?.authorId && (!user?.groups || user.groups?.groupId === group?.groupId)
  );

  return (
    <div className={styles['new-mini-group']}>
      <Form onFinish={onFinish} form={form} layout='vertical'>
        <Form.Item
          label={t('nameMiniGroup')}
          name='groupName'
          rules={[{ required: true, message: t('pleaseEnterGroupName') }]}
        >
          <Input count={{ show: true, max: MAX_LENGTH_NAME }} />
        </Form.Item>
        <Form.Item name='groupColor' label={t('colorMiniGroup')}>
          <ColorPicker disabledFormat format='hex' />
        </Form.Item>
        <div className={styles['new-mini-group__users']}>
          {filteredChatUsers.length ? (
            filteredChatUsers?.map(user => (
              <ChatUser
                key={user.id}
                chatId={selectedUser.chatId}
                data={user}
                canSelect
                onSelect={() => onSelectUser(user.id)}
                isSelected={selectedUsers.includes(user.id)}
                withOutGroup
              />
            ))
          ) : (
            <div className={styles['new-mini-group__not-users']}>
              <Typography.Text>{t('notUsers')}</Typography.Text>
            </div>
          )}
        </div>
        <div className={styles['new-mini-group__footer']}>
          {group && (
            <Button onClick={onDelete} type='primary' danger>
              {t('delete')}
            </Button>
          )}
          <div className={styles['new-mini-group__button-box']}>
            <Button ghost onClick={onCancel}>
              {t('cancel')}
            </Button>
            <Button htmlType='submit' disabled={isButtonDisabled} type='primary'>
              {t('ok')}
            </Button>
          </div>
        </div>
      </Form>
    </div>
  );
};

NewMiniGroup.propTypes = {
  onCancel: PropTypes.func,
  onSubmit: PropTypes.func,
  group: PropTypes.object,
};

export default NewMiniGroup;
